import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import './PersonalBrandingWidget.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function appendPercent(value) {
    if (typeof value === 'string' && value.includes('%')) {
        return value;
    }
    return `${value}%`;
}

function PersonalBrandingWidget({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const defaultProfileImage = '/img/personal-branding-profile.webp';
    const deleteLogo = '/img/delete.svg';

    const fonts = useFont();
    
    // INLINE-EDITING STATES
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const widgetRef = useRef(null);

    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    
    // Animation Trigger State (2025 Animation System)
    const [animationKey, setAnimationKey] = useState(0);
    
    const containerRef = useRef(null);
    const overlayRef = useRef(null);
    const wrapperRef = useRef(null);

    // 🚀 REVOLUTIONARY LAYOUT SYSTEM - Responsive Widget Styles
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none'
    });

    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Height-Property (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 700)
        })
    };

    // Animation Trigger Effect (2025 Animation System)
    useEffect(() => {
        if (data.triggerAnimation && isEditMode) {
            setAnimationKey(prev => prev + 1);
        }
    }, [data.triggerAnimation, isEditMode]);

    // INLINE-EDITING CORE FUNCTIONS
    const handleInlineEdit = (fieldName, currentValue) => {
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        if (isInlineEditing === fieldName) {
            handleInlineCancel(); // Toggle: Beenden wenn bereits aktiv
            return;
        }
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = useCallback((fieldName) => {
        if (!onUpdate) {
            console.warn('⚠️ onUpdate not available');
            return;
        }
        
        console.log('💾 PersonalBrandingWidget Saving field:', fieldName, 'with value:', inlineValue);
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        setIsInlineEditing(null);
        setInlineValue('');
        setOriginalLayout({});
        console.log('✅ PersonalBrandingWidget Save completed for:', fieldName);
    }, [onUpdate, data, inlineValue]);

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
        setOriginalLayout({});
    };

    const handleInlineKeyPress = (e, fieldName) => {
        const isTextarea = fieldName.includes('Question') || fieldName.includes('help') || fieldName.includes('bullet');
        
        if (e.key === 'Enter') {
            if (isTextarea) {
                if (e.ctrlKey || e.metaKey) {
                    handleInlineSubmit(fieldName);
                }
                // Normale Enter für neue Zeile
            } else {
                handleInlineSubmit(fieldName);
            }
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    const handleElementClick = (e, fieldName, currentValue) => {
        e.preventDefault();
        e.stopPropagation();
        
        const rect = e.target.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(e.target);
        
        // LEAN Layout-Erfassung - nur essenzielle Eigenschaften
        const layoutData = {
            width: rect.width + 'px',
            height: rect.height + 'px',
            marginTop: computedStyle.marginTop,
            marginBottom: computedStyle.marginBottom,
            marginLeft: computedStyle.marginLeft,
            marginRight: computedStyle.marginRight
        };
        
        setOriginalLayout(layoutData);
        handleInlineEdit(fieldName, currentValue);
    };

    const handleImageUpload = async (fieldName, setIsUploading) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (file && onUpdate) {
                setIsUploading(true);
                try {
                    const imageUrl = URL.createObjectURL(file);
                    const updatedData = { ...data, [fieldName]: imageUrl };
                    onUpdate(updatedData);
                } catch (error) {
                    console.error('Upload failed:', error);
                } finally {
                    setIsUploading(false);
                }
            }
        };
        input.click();
    };

    // SMART OUTSIDE-CLICK HANDLER
    useEffect(() => {
        if (!isInlineEditing) return;

        const handleOutsideClick = (event) => {
            console.log('🔍 PersonalBrandingWidget Outside Click Debug:', {
                target: event.target.tagName,
                isInlineEditing,
                inlineValue: inlineValue.substring(0, 20) + '...'
            });

            const isEditingInput = event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA';
            const isActionButton = event.target.hasAttribute('data-action-button') || event.target.closest('[data-action-button]');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit-container');
            const isWithinWidget = widgetRef.current?.contains(event.target);
            
            // Klick auf anderes Text-Element = Outside-Click
            const isClickOnOtherTextElement = event.target.tagName === 'P' || 
                                            event.target.tagName === 'H1' || 
                                            event.target.tagName === 'H2' || 
                                            event.target.tagName === 'H3' || 
                                            event.target.tagName === 'DIV' ||
                                            event.target.tagName === 'BUTTON';
            
            const isRealOutsideClick = (!isEditingInput && !isActionButton && !isDeleteButton && !isWidgetEdit && !isWithinWidget) ||
                                      (isClickOnOtherTextElement && isWithinWidget && !isEditingInput);

            console.log('🔍 PersonalBrandingWidget Outside Click Checks:', {
                isEditingInput,
                isActionButton,
                isDeleteButton,
                isWidgetEdit,
                isWithinWidget,
                isClickOnOtherTextElement,
                isRealOutsideClick
            });

            if (isRealOutsideClick && isInlineEditing) {
                console.log('✅ PersonalBrandingWidget Saving via outside click:', isInlineEditing, inlineValue);
                handleInlineSubmit(isInlineEditing);
            }
        };

        document.addEventListener('click', handleOutsideClick, true);
        return () => document.removeEventListener('click', handleOutsideClick, true);
    }, [isInlineEditing, inlineValue, handleInlineSubmit]);

    // 🚀 REVOLUTIONARY LAYOUT SYSTEM - Mobile Breakpoint Detection (750px)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750); // ✅ Standardized Breakpoint
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(() => {
            checkContainerWidth();
        });
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // RENDER EDITABLE TEXT FUNCTION (NUR FÜR TEXT-ELEMENTE)
    const renderEditableText = (fieldName, currentValue, Element, style, placeholder) => {
        const isEditing = isInlineEditing === fieldName;
        const layoutData = originalLayout;

        if (isEditing && isEditMode) {
            return (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        className="inline-editing-textarea"
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            // ✅ Cursor-Position merken BEVOR wir die Höhe ändern
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                // ✅ Cursor-Position wiederherstellen
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                        placeholder={placeholder}
                        ref={(element) => {
                            if (element) {
                                setTimeout(() => {
                                    // ✅ NUR beim ersten Laden ans Ende setzen, nicht bei jedem Re-Render
                                    if (element.selectionStart === 0 && element.selectionEnd === 0) {
                                        element.setSelectionRange(element.value.length, element.value.length);
                                    }
                                    element.style.height = 'auto';
                                    element.style.height = element.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: style.background || style.backgroundColor || 'transparent', // ✅ Gradient-Support
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '0',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            whiteSpace: 'pre-wrap',
                            color: style.color || '#333333', // ✅ Explizite Farb-Übertragung
                            fontSize: style.fontSize || 'inherit',
                            fontWeight: style.fontWeight || 'inherit',
                            fontFamily: style.fontFamily || 'inherit',
                            lineHeight: style.lineHeight || 'inherit'
                        }}
                    />
                    {/* Action Buttons */}
                    <div style={{
                        position: 'absolute',
                        top: '-11px',
                        right: '-6px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(4px)',
                        borderRadius: '6px',
                        padding: '2px 4px',
                        display: 'flex',
                        gap: '4px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        zIndex: 9999
                    }}>
                        <button 
                            data-action-button="save"
                            onClick={() => handleInlineSubmit(fieldName)}
                            style={{
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                padding: '0',
                                border: 'none',
                                background: '#10b981',
                                color: 'white',
                                fontSize: '10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        >✓</button>
                        <button 
                            data-action-button="cancel"
                            onClick={handleInlineCancel}
                            style={{
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                padding: '0',
                                border: 'none',
                                background: '#ef4444',
                                color: 'white',
                                fontSize: '10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        >×</button>
                    </div>
                </div>
            );
        }

        // Normal display with hover effects
        return React.createElement(Element, {
            style: {
                ...style,
                cursor: isEditMode ? 'text' : 'default',
                transition: 'all 0.3s ease',
                whiteSpace: 'pre-wrap'  // ✅ KRITISCH: Whitespace für Anzeige beibehalten
            },
            onClick: isEditMode ? (e) => handleElementClick(e, fieldName, currentValue) : undefined,
            onMouseEnter: isEditMode ? (e) => {
                e.target.style.background = 'linear-gradient(45deg, rgba(255, 255, 255, 0.02) 0%, rgba(192, 192, 192, 0.04) 25%, rgba(255, 255, 255, 0.06) 50%, rgba(192, 192, 192, 0.04) 75%, rgba(255, 255, 255, 0.02) 100%)';
                e.target.style.animation = 'silverGlow 3s ease-in-out infinite';
                e.target.style.backdropFilter = 'blur(8px)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            } : undefined,
            onMouseLeave: isEditMode ? (e) => {
                e.target.style.background = style.background || style.backgroundColor || 'transparent'; // ✅ Gradient-Support
                e.target.style.animation = 'none';
                e.target.style.backdropFilter = 'none';
                e.target.style.boxShadow = 'none';
            } : undefined
        }, currentValue || placeholder);
    };

    const handleButtonClick = () => {
        if (!isEditMode && data.buttonLink) {
            window.open(data.buttonLink, '_blank');
        }
    };

    // Get bullet icon based on style
    const getBulletIcon = (style = 'plus') => {
        const iconStyles = {
            plus: '+',
            check: '✓',
            arrow: '→',
            star: '★',
            diamond: '◆',
            circle: '●',
            heart: '♥',
            lightning: '⚡',
            crown: '♔',
            gem: '💎'
        };
        return iconStyles[style] || '+';
    };

    // Render bullet points dynamically with different styles
    const renderBulletPoints = () => {
        if (!data.bulletPoints || !Array.isArray(data.bulletPoints)) return null;
        
        const bulletStyle = data.bulletPointStyle || 'elegant';
        const bulletDesign = data.bulletPointDesign || 'default';
        
        return data.bulletPoints.map((bullet, index) => {
            // Different bullet styles
            const getStyleConfig = () => {
                switch (bulletStyle) {
                    case 'elegant':
                        return {
                            containerStyle: {
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '14px',
                                marginBottom: appendPx(data.bulletSpacing || 16),
                                padding: '12px 0',
                                transition: 'all 0.2s ease'
                            },
                            iconStyle: {
                                width: '18px',
                                height: '18px',
                                background: `${data.overlayTitleColor || '#D4A574'}`,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: '600',
                                flexShrink: 0,
                                marginTop: '4px',
                                opacity: 0.8
                            },
                            icon: '✓'
                        };
                    
                    case 'modern':
                        return {
                            containerStyle: {
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                marginBottom: appendPx(data.bulletSpacing || 14),
                                padding: '8px 0',
                                borderLeft: `2px solid ${data.overlayTitleColor || '#D4A574'}`,
                                paddingLeft: '12px',
                                opacity: 0.9
                            },
                            iconStyle: {
                                color: data.overlayTitleColor || '#D4A574',
                                fontSize: '14px',
                                fontWeight: '600',
                                marginTop: '3px',
                                flexShrink: 0,
                                opacity: 0.8
                            },
                            icon: ''
                        };
                    
                    case 'luxury':
                        return {
                            containerStyle: {
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '14px',
                                marginBottom: appendPx(data.bulletSpacing || 16),
                                padding: '12px 0',
                                position: 'relative'
                            },
                            iconStyle: {
                                fontSize: '16px',
                                color: data.overlayTitleColor || '#D4A574',
                                marginTop: '2px',
                                flexShrink: 0,
                                opacity: 0.7
                            },
                            icon: '♔'
                        };
                    
                    case 'minimal':
                        return {
                            containerStyle: {
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                marginBottom: appendPx(data.bulletSpacing || 14),
                                padding: '8px 0'
                            },
                            iconStyle: {
                                width: '6px',
                                height: '6px',
                                backgroundColor: data.overlayTitleColor || '#D4A574',
                                borderRadius: '50%',
                                marginTop: '8px',
                                flexShrink: 0
                            },
                            icon: ''
                        };
                    
                    default:
                        return {
                            containerStyle: {
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                marginBottom: appendPx(data.bulletSpacing || 16)
                            },
                            iconStyle: {
                                color: data.bulletPointColor || '#5A5A5A',
                                fontSize: appendPx(data.bulletPointSize || 14),
                                fontWeight: '600',
                                marginTop: '2px',
                                flexShrink: 0
                            },
                            icon: '+'
                        };
                }
            };

            const styleConfig = getStyleConfig();
            
            // Apply bullet design modifications
            const getBulletDesignStyle = (baseStyle) => {
                switch (bulletDesign) {
                    case 'cards':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: appendPx((data.bulletSpacing || 16) + 4),
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                        };
                    case 'bordered':
                        return {
                            ...baseStyle,
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            borderRadius: '4px',
                            padding: '8px 12px',
                            marginBottom: appendPx((data.bulletSpacing || 16) + 2)
                        };
                    case 'highlighted':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(212, 165, 116, 0.1)',
                            borderRadius: '6px',
                            padding: '10px 14px',
                            marginBottom: appendPx((data.bulletSpacing || 16) + 2)
                        };
                    case 'rounded':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(0, 0, 0, 0.06)',
                            borderRadius: '20px',
                            padding: '8px 16px',
                            marginBottom: appendPx((data.bulletSpacing || 16) + 2)
                        };
                    case 'gradient':
                        return {
                            ...baseStyle,
                            background: 'linear-gradient(135deg, rgba(212, 165, 116, 0.15) 0%, rgba(212, 165, 116, 0.05) 100%)',
                            border: '1px solid rgba(212, 165, 116, 0.2)',
                            borderRadius: '12px',
                            padding: '12px 16px',
                            marginBottom: appendPx((data.bulletSpacing || 16) + 4),
                            position: 'relative',
                            overflow: 'hidden'
                        };
                    case 'vintage':
                        return {
                            ...baseStyle,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '2px solid rgba(139, 115, 85, 0.3)',
                            borderRadius: '4px',
                            padding: '10px 14px',
                            marginBottom: appendPx((data.bulletSpacing || 16) + 4),
                            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 2px 4px rgba(139, 115, 85, 0.2)',
                            position: 'relative'
                        };
                    default:
                        return baseStyle;
                }
            };

            return (
                <div 
                    key={`${bullet.id || index}-${animationKey}`}
                    className={data.enableAnimations && isEditMode ? `personal-branding-animated ${data.animationType || 'fadeInUp'}` : ''}
                    style={{
                        ...getBulletDesignStyle(styleConfig.containerStyle),
                        animationDuration: data.enableAnimations && isEditMode ? `${data.animationDuration || 600}ms` : '0s',
                        animationDelay: data.enableAnimations && isEditMode ? `${index * (data.animationDelay || 150)}ms` : '0s'
                    }}
                >
                    {/* Bullet Icon */}
                    <div style={styleConfig.iconStyle}>
                        {styleConfig.icon}
                    </div>
                    
                    {/* Bullet Text */}
                    <p 
                        key={`bullet-${index}-${data.bulletPointEffect || 'none'}`}
                        className="responsive-bullet-text"
                        style={{
                            ...((!data.bulletPointEffect || data.bulletPointEffect === 'none') && {
                                color: data.bulletPointColor || '#5A5A5A'
                            }),
                            fontSize: appendPx(data.bulletPointSize || 15),
                            fontWeight: data.bulletPointWeight || '400',
                            fontFamily: data.bulletPointFont || fonts.globalSmallFont,
                            lineHeight: '1.6',
                            margin: '0',
                            letterSpacing: '0.2px',
                            ...getTextEffectStyles(data.bulletPointEffect),
                            // Responsive Font-Sizes
                            '--mobile-size': appendPx(data.bulletPointSizeMobile || data.bulletPointSize || 15),
                            '--normal-size': appendPx(data.bulletPointSize || 15),
                            '--big-size': appendPx(data.bulletPointSizeBig || data.bulletPointSize || 15)
                        }}>
                        {bullet.text}
                    </p>
                </div>
            );
        });
    };

    return (
        <>
            <style>
                {`
                    .inline-editing-textarea {
                        caret-color: currentColor;
                    }
                    
                    @keyframes silverGlow {
                        0% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                        50% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.04) 0%,
                                rgba(192, 192, 192, 0.05) 25%,
                                rgba(255, 255, 255, 0.06) 50%,
                                rgba(192, 192, 192, 0.05) 75%,
                                rgba(255, 255, 255, 0.04) 100%
                            );
                        }
                        100% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                    }
                    @keyframes personalBrandingImageUploadSpin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    /* 🚀 REVOLUTIONARY BUTTON ANIMATIONS 2025 */
                    @keyframes gradientShift {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    }
                    
                    @keyframes gentle-pulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.02); opacity: 0.9; }
                    }

                    @keyframes gentle-sway {
                        0%, 100% { transform: translateX(0px); }
                        25% { transform: translateX(-2px); }
                        75% { transform: translateX(2px); }
                    }

                    @keyframes subtle-float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-3px); }
                    }

                    @keyframes gentle-breathe {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.03); opacity: 0.85; }
                    }

                    @keyframes gentle-wobble {
                        0%, 100% { transform: rotate(0deg); }
                        25% { transform: rotate(-1deg); }
                        75% { transform: rotate(1deg); }
                    }
                    
                    /* Personal Branding Animation Keyframes (2025 Animation System) */
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    @keyframes slideIn {
                        from {
                            opacity: 0;
                            transform: translateX(-30px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    
                    @keyframes fadeInSlow {
                        0% { opacity: 0; }
                        30% { opacity: 0; }
                        100% { opacity: 1; }
                    }
                    
                    /* Personal Branding Animation Classes */
                    .personal-branding-animated {
                        animation-fill-mode: both;
                    }
                    
                    .personal-branding-animated.fadeInUp {
                        animation-name: fadeInUp;
                    }
                    
                    .personal-branding-animated.slideIn {
                        animation-name: slideIn;
                    }
                    
                    .personal-branding-animated.fadeIn {
                        animation-name: fadeIn;
                    }
                    
                    .personal-branding-animated.fadeInSlow {
                        animation-name: fadeInSlow;
                    }
                `}
            </style>
            <div 
                className="personal-branding-widget" 
                style={widgetStyle} 
                ref={(el) => { containerRef.current = el; widgetRef.current = el; }}
            >
            {/* Delete Button */}
            {isEditMode && (
                <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                    <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                </button>
            )}
            
            {/* 🤖 AI REFRESH ICON */}
            {isEditMode && (
                <AIRefreshIcon 
                    widget={{
                        ...data,
                        type: 'PersonalBrandingWidget'
                    }}
                    spaceId={null}
                    onContentRefreshed={(refreshedWidget) => {
                        console.log('🤖 [PERSONALBRANDINGWIDGET] AI-refreshed content received:', refreshedWidget);
                        onUpdate(refreshedWidget);
                    }}
                    position="top-left"
                />
            )}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={(event) => handleDelete(event, data.id)}
                />
            )}
            
            {/* Main Container - 🚀 Revolutionary Layout System */}
            <div style={{
                paddingTop: appendPx(getResponsiveValue(data, 'paddingTop', isMobileLayout, isMobileLayout ? 100 : 100)),
                paddingBottom: appendPx(getResponsiveValue(data, 'paddingBottom', isMobileLayout, isMobileLayout ? 100 : 100)),
                paddingLeft: isMobileLayout ? '20px' : '40px',
                paddingRight: isMobileLayout ? '20px' : '40px',
                position: 'relative',
                minHeight: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start'
            }}>
                
                {/* Content Wrapper for Centering */}
                <div 
                    ref={wrapperRef}
                    style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: isMobileLayout ? '100%' : '800px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: isMobileLayout ? '0px' : '0px'
                    }}>
                    {/* Left Side: Name Above Image + Image + Description */}
                    <div 
                        className="personal-branding-image-container"
                        style={{
                            width: isMobileLayout ? '100%' : '320px',
                            marginBottom: isMobileLayout ? '30px' : '0',
                            margin: isMobileLayout ? '0 auto 30px auto' : '0 0 0 0',
                            transform: isMobileLayout ? 'rotate(-1.5deg)' : 'rotate(-3deg)',
                            transformOrigin: 'center center',
                            zIndex: 1
                        }}>
                    {/* Name Above Image - Mit Show/Hide Toggle */}
                    {data.showOverlayName !== false && data.overlayName && renderEditableText(
                        'overlayName',
                        data.overlayName,
                        'h2',
                        {
                            ...((!data.overlayNameEffect || data.overlayNameEffect === 'none') && {
                                color: data.overlayNameColor || '#8B7355'
                            }),
                            fontSize: appendPx(data.overlayNameSize || 36),
                            fontWeight: data.overlayNameWeight || '400',
                            fontFamily: data.overlayNameFont || fonts.globalBigFont,
                            fontStyle: data.overlayNameStyle || '',
                            marginBottom: '-20px',
                            position: 'relative',
                            zIndex: '100',
                            lineHeight: '1.2',
                            textAlign: isMobileLayout ? 'center' : 'left',
                            ...getTextEffectStyles(data.overlayNameEffect),
                            // Responsive Font-Sizes
                            '--mobile-size': appendPx(data.overlayNameSizeMobile || data.overlayNameSize || 36),
                            '--normal-size': appendPx(data.overlayNameSize || 36),
                            '--big-size': appendPx(data.overlayNameSizeBig || data.overlayNameSize || 36)
                        },
                        'Enter your name...'
                    )}

                    {/* Profile Image */}
                    <div style={{
                        marginBottom: '20px'
                    }}>
                        {data.profileImage && (
                            <img 
                                src={data.profileImage || defaultProfileImage}
                                alt="Profile"
                                style={{
                                    width: isMobileLayout ? '100%' : '400px',
                                    height: appendPx(data.profileImageHeight || 600),
                                    objectFit: 'cover',
                                    borderRadius: '8px'
                                }}
                            />
                        )}
                    </div>

                    {/* Image Description - nur auf Desktop anzeigen - Mit Show/Hide Toggle */}
                    {!isMobileLayout && data.showImageDescription !== false && data.imageDescription && (
                        <p 
                            className="responsive-image-description"
                            style={{
                                color: data.imageDescriptionColor || '#666666',
                                fontSize: appendPx(data.imageDescriptionSize || 12),
                                fontWeight: data.imageDescriptionWeight || '400',
                                fontFamily: data.imageDescriptionFont || fonts.globalSmallFont,
                                lineHeight: '1.4',
                                maxWidth: '400px',
                                textAlign: 'center',
                                // Responsive Font-Sizes
                                '--normal-size': appendPx(data.imageDescriptionSize || 12),
                                '--big-size': appendPx(data.imageDescriptionSizeBig || data.imageDescriptionSize || 12)
                            }}>
                            {data.imageDescription}
                        </p>
                    )}
                </div>

                    {/* Right Side: Overlay Container with Content */}
                    <div 
                        ref={overlayRef}
                        className={`personal-branding-overlay-container ${data.containerLayout ? `layout-${data.containerLayout}` : 'layout-glass-card'}`}
                        style={{
                            '--accent-color': data.overlayTitleColor || '#D4A574',
                            '--dynamic-bg-color': data.backgroundColor || 'rgba(255, 255, 255, 0.95)', // 🎯 DYNAMIC BACKGROUND
                            width: isMobileLayout ? '100%' : '450px',
                            maxWidth: isMobileLayout ? '100%' : '450px',
                            // AI-FARBEN: Background wird von AI gesetzt - kein hardcoded background
                            backgroundColor: data.backgroundColor, // ✅ Direkt die AI-Farbe verwenden
                            backdropFilter: 'blur(10px)',
                            borderRadius: '16px',
                            padding: isMobileLayout ? `${data.containerPadding || 35}px 20px` : `${data.containerPadding || 35}px`,
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            zIndex: 10,
                            marginTop: isMobileLayout ? '-140px' : '-500px',
                            marginLeft: isMobileLayout ? 'auto' : '360px',
                            marginRight: isMobileLayout ? 'auto' : '0'
                        }}>
                    {/* Subtle Titles Section */}
                    <div style={{ 
                        marginBottom: '40px',
                        textAlign: 'center'
                    }}>
                        {data.showOverlayTitle1 !== false && data.overlayTitle1 && (
                            <p 
                                key={`overlay-title1-${data.overlayTitleEffect || 'none'}`}
                                className="responsive-overlay-title"
                                style={{
                                    ...((!data.overlayTitleEffect || data.overlayTitleEffect === 'none') && {
                                        color: data.containerLayout === 'neon-glow' ? '#ffffff' : (data.overlayTitleColor || '#8A8A8A')
                                    }),
                                    fontSize: appendPx(data.overlayTitleSize || 18),
                                    fontWeight: data.overlayTitleWeight || '400',
                                    fontFamily: data.overlayTitleFont || fonts.globalSmallFont,
                                    fontStyle: data.overlayTitleStyle || 'italic',
                                    marginBottom: '6px',
                                    lineHeight: '1.5',
                                    letterSpacing: '0.3px',
                                    opacity: 0.9,
                                    ...getTextEffectStyles(data.overlayTitleEffect),
                                    // Responsive Font-Sizes
                                    '--mobile-size': appendPx(data.overlayTitleSizeMobile || data.overlayTitleSize || 18),
                                    '--normal-size': appendPx(data.overlayTitleSize || 18),
                                    '--big-size': appendPx(data.overlayTitleSizeBig || data.overlayTitleSize || 18)
                                }}>
                                {data.overlayTitle1}
                            </p>
                        )}

                        {data.showOverlayTitle2 !== false && data.overlayTitle2 && (
                            <p 
                                key={`overlay-title2-${data.overlayTitleEffect || 'none'}`}
                                className="responsive-overlay-title"
                                style={{
                                    ...((!data.overlayTitleEffect || data.overlayTitleEffect === 'none') && {
                                        color: data.overlayTitleColor || '#8A8A8A'
                                    }),
                                    fontSize: appendPx(data.overlayTitleSize || 18),
                                    fontWeight: data.overlayTitleWeight || '400',
                                    fontFamily: data.overlayTitleFont || fonts.globalSmallFont,
                                    fontStyle: data.overlayTitleStyle || 'italic',
                                    marginBottom: '6px',
                                    lineHeight: '1.5',
                                    letterSpacing: '0.3px',
                                    opacity: 0.9,
                                    ...getTextEffectStyles(data.overlayTitleEffect),
                                    // Responsive Font-Sizes
                                    '--mobile-size': appendPx(data.overlayTitleSizeMobile || data.overlayTitleSize || 18),
                                    '--normal-size': appendPx(data.overlayTitleSize || 18),
                                    '--big-size': appendPx(data.overlayTitleSizeBig || data.overlayTitleSize || 18)
                                }}>
                                {data.overlayTitle2}
                            </p>
                        )}

                        {data.showOverlayTitle3 !== false && data.overlayTitle3 && (
                            <p 
                                key={`overlay-title3-${data.overlayTitleEffect || 'none'}`}
                                className="responsive-overlay-title"
                                style={{
                                    ...((!data.overlayTitleEffect || data.overlayTitleEffect === 'none') && {
                                        color: data.overlayTitleColor || '#8A8A8A'
                                    }),
                                    fontSize: appendPx(data.overlayTitleSize || 18),
                                    fontWeight: data.overlayTitleWeight || '400',
                                    fontFamily: data.overlayTitleFont || fonts.globalSmallFont,
                                    fontStyle: data.overlayTitleStyle || 'italic',
                                    marginBottom: '0',
                                    lineHeight: '1.5',
                                    letterSpacing: '0.3px',
                                    opacity: 0.9,
                                    ...getTextEffectStyles(data.overlayTitleEffect),
                                    // Responsive Font-Sizes
                                    '--mobile-size': appendPx(data.overlayTitleSizeMobile || data.overlayTitleSize || 18),
                                    '--normal-size': appendPx(data.overlayTitleSize || 18),
                                    '--big-size': appendPx(data.overlayTitleSizeBig || data.overlayTitleSize || 18)
                                }}>
                                {data.overlayTitle3}
                            </p>
                        )}
                    </div>

                    {/* Main Question - Mit Show/Hide Toggle */}
                    {data.showMainQuestion !== false && data.mainQuestion && renderEditableText(
                        'mainQuestion',
                        data.mainQuestion,
                        'h1',
                        {
                            ...((!data.mainQuestionEffect || data.mainQuestionEffect === 'none') && {
                                color: data.containerLayout === 'neon-glow' ? '#ffffff' : (data.mainQuestionColor || '#4A4A4A')
                            }),
                            fontSize: appendPx(data.mainQuestionSize || 34),
                            fontWeight: data.mainQuestionWeight || '600',
                            fontFamily: data.mainQuestionFont || fonts.globalBigFont,
                            marginBottom: '35px',
                            lineHeight: '1.3',
                            letterSpacing: '-0.5px',
                            textShadow: '0 2px 4px rgba(0,0,0,0.08)',
                            textAlign: 'center',
                            ...getTextEffectStyles(data.mainQuestionEffect),
                            // Responsive Font-Sizes
                            '--mobile-size': appendPx(data.mainQuestionSizeMobile || data.mainQuestionSize || 34),
                            '--normal-size': appendPx(data.mainQuestionSize || 34),
                            '--big-size': appendPx(data.mainQuestionSizeBig || data.mainQuestionSize || 34)
                        },
                        'Enter your main question...'
                    )}

                    {/* Help Text - Mit Show/Hide Toggle */}
                    {data.showHelpText !== false && data.helpText && renderEditableText(
                        'helpText',
                        data.helpText,
                        'p',
                        {
                            color: data.helpTextColor || '#666666',
                            fontSize: appendPx(data.helpTextSize || 16),
                            fontWeight: data.helpTextWeight || '400',
                            fontFamily: data.helpTextFont || fonts.globalSmallFont,
                            marginBottom: '20px',
                            lineHeight: '1.5',
                            textAlign: 'center',
                            // Responsive Font-Sizes
                            '--mobile-size': appendPx(data.helpTextSizeMobile || data.helpTextSize || 16),
                            '--normal-size': appendPx(data.helpTextSize || 16),
                            '--big-size': appendPx(data.helpTextSizeBig || data.helpTextSize || 16)
                        },
                        'Enter help text...'
                    )}

                    {/* Bullet Points */}
                    <div style={{ marginBottom: '40px' }}>
                        {renderBulletPoints()}
                    </div>

                    {/* CTA Button with Revolutionary Button System 2025 */}
                    <div style={{ textAlign: 'center' }}>
                        {data.showButtonText !== false && (
                            <button
                                onClick={!isEditMode ? handleButtonClick : (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isInlineEditing === 'buttonText') {
                                        handleInlineCancel();
                                        return;
                                    }
                                    handleInlineEdit('buttonText', data.buttonText);
                                }}
                                onMouseEnter={!isEditMode ? (e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = data.buttonShadowEnabled 
                                        ? `0 ${(data.buttonShadowDistance || 8) + 4}px ${(data.buttonShadowIntensity || 15) + 8}px ${data.buttonShadowColor || '#000000'}${Math.round(((data.buttonShadowOpacity || 25) + 10) * 2.55).toString(16).padStart(2, '0')}`
                                        : '0 6px 16px rgba(0, 0, 0, 0.2)';
                                } : undefined}
                                onMouseLeave={!isEditMode ? (e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = data.buttonShadowEnabled 
                                        ? `0 ${data.buttonShadowDistance || 8}px ${data.buttonShadowIntensity || 15}px ${data.buttonShadowColor || '#000000'}${Math.round((data.buttonShadowOpacity || 25) * 2.55).toString(16).padStart(2, '0')}`
                                        : 'none';
                                } : undefined}
                                style={{
                                // 🎨 GRADIENT vs. SOLID COLOR
                                background: data.buttonGradientEnabled 
                                    ? `linear-gradient(135deg, ${data.buttonGradientColor1 || '#667eea'} 0%, ${data.buttonGradientColor2 || '#764ba2'} 100%)`
                                    : (data.buttonColor || '#8B7355'),
                                    
                                // 🎬 ANIMATIONS
                                backgroundSize: data.buttonGradientEnabled && data.buttonGradientAnimation ? '300% 300%' : 'auto',
                                animation: data.buttonGradientEnabled && data.buttonGradientAnimation 
                                    ? 'gradientShift 4s ease-in-out infinite' 
                                    : data.buttonAnimationsEnabled && data.buttonAnimationType && data.buttonAnimationType !== 'none'
                                    ? `${data.buttonAnimationType} ${data.buttonAnimationSpeed || 2000}ms ease-in-out infinite`
                                    : 'none',
                                    
                                // 🎨 COLORS & STYLING
                                color: data.buttonTextColor || '#FFFFFF',
                                
                                // 🔲 BORDER
                                border: data.buttonBorderEnabled && data.buttonBorderWidth && data.buttonBorderWidth > 0
                                    ? `${data.buttonBorderWidth}px solid ${data.buttonBorderColor || '#8B7355'}`
                                    : 'none',
                                    
                                // 🌟 SHADOW
                                boxShadow: data.buttonShadowEnabled 
                                    ? `0 ${data.buttonShadowDistance || 8}px ${data.buttonShadowIntensity || 15}px ${data.buttonShadowColor || '#000000'}${Math.round((data.buttonShadowOpacity || 25) * 2.55).toString(16).padStart(2, '0')}`
                                    : 'none',
                                    
                                // 📐 RESPONSIVE DIMENSIONS
                                fontSize: isMobileLayout 
                                    ? appendPx(data.buttonTextSizeMobile || data.buttonTextSize || 14) 
                                    : appendPx(data.buttonTextSize || 14),
                                fontWeight: isMobileLayout 
                                    ? (data.buttonTextWeightMobile || data.buttonTextWeight || '600') 
                                    : (data.buttonTextWeight || '600'),
                                fontFamily: data.buttonFont || fonts.globalSmallFont,
                                width: isMobileLayout 
                                    ? appendPercent(data.buttonWidthMobile || data.buttonWidth || 100) 
                                    : appendPercent(data.buttonWidth || 100),
                                maxWidth: isMobileLayout 
                                    ? appendPx(data.buttonMaxWidthMobile || data.buttonMaxWidth || 250) 
                                    : appendPx(data.buttonMaxWidth || 250),
                                height: isMobileLayout 
                                    ? appendPx(data.buttonHeightMobile || data.buttonHeight || 50) 
                                    : appendPx(data.buttonHeight || 50),
                                borderRadius: isMobileLayout 
                                    ? appendPx(data.buttonBorderRadiusMobile || data.buttonBorderRadius || 4) 
                                    : appendPx(data.buttonBorderRadius || 4),
                                    
                                // 🎯 LAYOUT PROPERTIES
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isEditMode ? 'default' : 'pointer',
                                transition: 'all 0.3s ease',
                                textDecoration: 'none',
                                outline: 'none',
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                
                                // 🎯 HOVER EFFECTS werden über onMouseEnter/onMouseLeave Events gehandhabt
                            }}
                                title={isEditMode ? "Click to edit button text" : undefined}
                            >
                                {isInlineEditing === 'buttonText' ? (
                                    <input
                                        type="text"
                                        value={inlineValue}
                                        onChange={(e) => setInlineValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleInlineSubmit('buttonText');
                                            } else if (e.key === 'Escape') {
                                                handleInlineCancel();
                                            }
                                        }}
                                        onBlur={() => handleInlineSubmit('buttonText')}
                                        autoFocus
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            outline: 'none',
                                            color: 'inherit',
                                            fontSize: 'inherit',
                                            fontWeight: 'inherit',
                                            fontFamily: 'inherit',
                                            textAlign: 'center',
                                            width: '100%',
                                            letterSpacing: 'inherit',
                                            textTransform: 'inherit'
                                        }}
                                        placeholder="Enter button text..."
                                    />
                                ) : (
                                    data.buttonText || 'Enter button text...'
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
        </div>
        </>
    );
}

export default PersonalBrandingWidget;
